import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlatformLogout } from "../../../store/authSlice";
import L from "../../../constants/baysportLabels";
import logo from "../../../assets/logo.png";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";
import { usePwaChrome } from "../../../utils/pwaThemeColor";
import { useStandalone } from "../../../hooks/useStandalone";
import platformAPI from "../../../services/platformApi";
import PlatformAdminLoginPage from "../PlatformAdminLoginPage";
import { PlatformNotificationsBell } from "./PlatformNotifications";
import PlatformPwaBanners from "./PlatformPwaBanners";
import {
  PLATFORM_BOTTOM_TABS,
  isPlatformChatConversation,
  platformNavIcon,
  resolvePlatformBottomTab,
} from "../../../utils/platformNav";
import s from "./platformAdmin.module.css";

const PLATFORM_PAGE_BG = "#f1f5f9";

const SIDEBAR_ANALYTICS = [
  { to: "/admin", end: true, label: "Сводка", icon: "overview" },
  { to: "/admin/funnel", label: "Воронка", icon: "funnel" },
];

const SIDEBAR_OPS = [
  { to: "/admin/tenants", label: "Клубы", icon: "clients" },
  { to: "/admin/payments", label: "Оплаты", icon: "finance" },
  { to: "/admin/support", label: "Поддержка", icon: "chat" },
];

const SIDEBAR_SYSTEM = [
  { to: "/admin/plans", label: "Тарифы", icon: "memberships" },
  { to: "/admin/notifications", label: "Уведомления", icon: "notifications" },
  { to: "/admin/audit", label: "Журнал", icon: "journal" },
];

function PlatformBottomNav({ supportBadge, notifyBadge }) {
  const { pathname } = useLocation();
  const active = resolvePlatformBottomTab(pathname);

  return (
    <nav className={s.appTabBar} aria-label="Навигация платформы">
      {PLATFORM_BOTTOM_TABS.map((item) => {
        const Icon = platformNavIcon(item.icon);
        const isActive = active === item.id;
        let badge = 0;
        if (item.id === "support" && supportBadge > 0) badge = supportBadge;
        if (item.id === "more" && notifyBadge > 0) badge = notifyBadge;
        return (
          <NavLink
            key={item.id}
            to={item.to}
            end={item.end}
            className={
              isActive ? `${s.appTabItem} ${s.appTabItemActive}` : s.appTabItem
            }
          >
            <span className={s.appTabIconWrap}>
              <Icon className={s.appTabIcon} />
              {badge > 0 ? (
                <span className={s.appTabBadge}>
                  {badge > 99 ? "99+" : badge}
                </span>
              ) : null}
            </span>
            <span className={s.appTabLabel}>{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function SidebarLink({ item, supportBadge, notifyBadge }) {
  const Icon = platformNavIcon(item.icon);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        isActive ? `${s.appNavLink} ${s.appNavLinkActive}` : s.appNavLink
      }
    >
      <Icon className={s.appNavLinkIcon} />
      <span className={s.appNavLinkLabel}>{item.label}</span>
      {item.to === "/admin/support" && supportBadge > 0 ? (
        <span className={s.appNavBadge}>
          {supportBadge > 99 ? "99+" : supportBadge}
        </span>
      ) : null}
      {item.to === "/admin/notifications" && notifyBadge > 0 ? (
        <span className={s.appNavBadge}>
          {notifyBadge > 99 ? "99+" : notifyBadge}
        </span>
      ) : null}
    </NavLink>
  );
}

export default function PlatformAdmin() {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useIsMobile(900);
  const standalone = useStandalone();
  const { isAuth, isPlatformAdmin, profile } = useSelector((state) => state.auth);
  const [ready, setReady] = useState(false);
  const [supportBadge, setSupportBadge] = useState(0);
  const [notifyBadge, setNotifyBadge] = useState(0);

  usePortalHtmlClass("platform");
  usePwaChrome(PLATFORM_PAGE_BG, {
    enabled: isMobile || standalone,
    isDark: false,
    immersive: false,
  });

  useEffect(() => {
    setReady(Boolean(isAuth && isPlatformAdmin));
  }, [isAuth, isPlatformAdmin]);

  const loadSupportBadge = useCallback(async () => {
    try {
      const data = await platformAPI.getSupportUnread();
      setSupportBadge(Number(data.unread || data.unread_count || 0) || 0);
    } catch {
      setSupportBadge(0);
    }
  }, []);

  const loadNotifyBadge = useCallback(async () => {
    try {
      const data = await platformAPI.getNotificationsUnread();
      setNotifyBadge(Number(data.unread || 0) || 0);
    } catch {
      setNotifyBadge(0);
    }
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    loadSupportBadge();
    loadNotifyBadge();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadSupportBadge();
        loadNotifyBadge();
      }
    }, 12000);
    const onActivity = () => {
      loadSupportBadge();
      loadNotifyBadge();
    };
    window.addEventListener("baysport:support-activity", onActivity);
    window.addEventListener("baysport:platform-notify", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:support-activity", onActivity);
      window.removeEventListener("baysport:platform-notify", onActivity);
    };
  }, [ready, loadSupportBadge, loadNotifyBadge, location.pathname]);

  const chatOpen = isPlatformChatConversation(location.pathname);

  useEffect(() => {
    if (!chatOpen) return undefined;
    document.documentElement.classList.add("platform-chat-open");
    return () => {
      document.documentElement.classList.remove("platform-chat-open");
    };
  }, [chatOpen]);

  if (!isAuth || !isPlatformAdmin) {
    return <PlatformAdminLoginPage />;
  }

  if (!ready) {
    return (
      <div className={s.loginWrap}>
        <div className={s.loginCard}>
          <p style={{ margin: 0 }}>Загрузка…</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={[
        s.appShell,
        isMobile ? s.appShellMobile : s.appShellDesktop,
        chatOpen ? s.appShellChatOpen : "",
        standalone ? s.appShellStandalone : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!isMobile ? (
        <aside className={s.appSidebar}>
          <Link to="/admin" className={s.appBrand}>
            <span className={s.appBrandMark}>
              <img src={logo} alt="" />
            </span>
            <span className={s.appBrandText}>
              <strong>{L.productName}</strong>
              <small>Admin</small>
            </span>
          </Link>

          <div className={s.appAdminBadge}>
            <span className={s.appAdminDot} aria-hidden />
            <div>
              <strong>Платформа</strong>
              <span>{profile?.email || "admin"}</span>
            </div>
          </div>

          <nav className={s.appSidebarNav} aria-label="Основное">
            <div className={s.appNavSection}>Аналитика</div>
            {SIDEBAR_ANALYTICS.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                supportBadge={supportBadge}
                notifyBadge={notifyBadge}
              />
            ))}

            <div className={s.appNavSection}>Операции</div>
            {SIDEBAR_OPS.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                supportBadge={supportBadge}
                notifyBadge={notifyBadge}
              />
            ))}

            <div className={s.appNavSection}>Система</div>
            {SIDEBAR_SYSTEM.map((item) => (
              <SidebarLink
                key={item.to}
                item={item}
                supportBadge={supportBadge}
                notifyBadge={notifyBadge}
              />
            ))}
          </nav>

          <div className={s.appSidebarFooter}>
            <a href="/" className={s.appNavLink}>
              На сайт
            </a>
            <button
              type="button"
              className={`${s.appNavLink} ${s.appNavLogout}`}
              onClick={() => dispatch(fetchPlatformLogout())}
            >
              Выйти
            </button>
          </div>
        </aside>
      ) : null}

      <div className={s.appBody}>
        {isMobile && !chatOpen ? (
          <header className={s.appTopBar}>
            <Link to="/admin" className={s.appTopBrand}>
              <img src={logo} alt="" />
              <span>{L.productName}</span>
            </Link>
            <div className={s.appTopActions}>
              <PlatformNotificationsBell unread={notifyBadge} />
            </div>
          </header>
        ) : null}

        {!isMobile ? (
          <header className={s.desktopTopBar}>
            <PlatformNotificationsBell unread={notifyBadge} />
          </header>
        ) : null}

        <main className={s.appMain}>
          <Outlet />
        </main>

        {isMobile && !chatOpen ? (
          <>
            <PlatformPwaBanners />
            <PlatformBottomNav
              supportBadge={supportBadge}
              notifyBadge={notifyBadge}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
